import type { ButtonProps } from '@mui/material/Button';
import { createContext, type MouseEventHandler, useContext } from 'react';
import type { UploadPartyPhotoModalProps } from '../components/common/upload-party-photo-modal';
import type { UpsertNoteModalProps } from '../components/students/note/upsert-note-modal';

export type NeedToKnowGroup = {
  partyId: number;
  name: string;
};

export type StudentActionsContextValue = {
  getNeedToKnowButtonProps: (
    studentPartyId: number,
    group?: NeedToKnowGroup,
  ) => ButtonProps;
  openNeedToKnowForStudent: (
    studentPartyId: number,
    group?: NeedToKnowGroup,
  ) => void;
  getCreateNoteButtonProps: (
    studentPartyId: number,
    additionalOptions?: AdditionalActionOptions,
  ) => ButtonProps;
  getEditNoteButtonProps: (
    studentPartyId: number,
    initialState: UpsertNoteModalProps['initialState'],
    additionalOptions?: AdditionalActionOptions,
  ) => ButtonProps;
  getCreateBehaviourButtonProps: (
    defaultState: {
      defaultStudentPartyIds?: number[];
      associatedGroups?: NeedToKnowGroup[];
    },
    additionalOptions?: AdditionalActionOptions,
  ) => ButtonProps;
  getEditBehaviourButtonProps: (
    behaviourId: number,
    additionalOptions?: AdditionalActionOptions,
  ) => ButtonProps;
  getAvatarUploadButtonProps: (
    uploadSettings: Pick<UploadPartyPhotoModalProps, 'partyId' | 'uploadType'>,
    additionalOptions?: AdditionalActionOptions,
  ) => ButtonProps;
  getAddQuickActionsButtonProps: (
    additionalOptions?: AdditionalActionOptions,
  ) => ButtonProps;
};

type AdditionalActionOptions = {
  onClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  onClose?: () => void;
  onNavigate?: () => void;
};

export const blankContextValue: StudentActionsContextValue = {
  getNeedToKnowButtonProps: () => ({}),
  openNeedToKnowForStudent: () => {},
  getCreateNoteButtonProps: () => ({}),
  getEditNoteButtonProps: () => ({}),
  getCreateBehaviourButtonProps: () => ({}),
  getEditBehaviourButtonProps: () => ({}),
  getAvatarUploadButtonProps: () => ({}),
  getAddQuickActionsButtonProps: () => ({}),
};

export const StudentActionsContext = createContext<
  StudentActionsContextValue | undefined
>(undefined);

export function useStudentActions() {
  const context = useContext(StudentActionsContext);
  if (!context) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        'useStudentActions must be used within a StudentActionsProvider. But if this is when you are exporting the component to HTML, then it is fine.',
      );
    }
    return blankContextValue;
  }

  return context ?? blankContextValue;
}
